/**
 * CAP install scheduling: sidebar copy + scheduler. Parent gates visibility with
 * `shouldShowPostCapInstallScheduler(prospect)` (`scheduleAtCAP === "Eligible"`).
 */
import React from "react";
import styled from "@emotion/styled/macro";
import { Typography } from "@sunrun/experience-ui-components";
import { useFlags } from "flagsmith/react";
import { isCapPackageCustomerSigned } from "../../constants/capPackageStatus";
import { useCapContext } from "../../providers/CapContext";
import { usePostCapInstallScheduling } from "../../hooks/usePostCapInstallScheduling";
import { useInstallSchedulingDaysOut } from "../../hooks/useInstallSchedulingDaysOut";
import {
  getInstallServiceAppointmentId,
  getPostCapInstallSectionDescription,
  DEFAULT_INSTALL_SCHEDULING_DAYS_OUT,
  isInstallServiceAppointmentCancelled,
  POST_CAP_INSTALL_CANCELLED_WARNING_MESSAGE,
  POST_CAP_INSTALL_EMPTY_SLOTS_MESSAGE,
  POST_CAP_INSTALL_PPC_PREREQUISITE_MESSAGE,
  POST_CAP_INSTALL_SECTION_TITLE,
  shouldFetchInstallSchedulingData,
} from "../../constants/capInstallScheduling";
import { POST_CAP_INSTALL_TEST_IDS } from "../../../checkout/components/molecules/siteSurveyScheduling";
import { isPpcSchedulingRequired } from "../../constants/postCapHomeCheck";
import { PostCapInstallScheduler } from "./PostCapInstallScheduler";
import { FeatureFlags } from "fixtures/features";

type PostCapInstallSectionProps = {
  docTextTitleSize: number;
  docTextDescriptionSize: number;
  isTablet: boolean;
};

const PostCapInstallSection: React.FC<PostCapInstallSectionProps> = ({
  docTextTitleSize,
  docTextDescriptionSize,
  isTablet,
}) => {
  const { prospectId, prospect, sow, documents, isSowLoading, mutateSow } =
    useCapContext();
  const canScheduleInstall = isCapPackageCustomerSigned(documents);
  const isPpcSchedulingInOneEnabled =
    useFlags([FeatureFlags.PPC_SCHEDULING_IN_ONE])[
      FeatureFlags.PPC_SCHEDULING_IN_ONE
    ]?.enabled ?? false;
  const isPpcSchedulingNecessary =
    isPpcSchedulingInOneEnabled &&
    isPpcSchedulingRequired(sow?.otherCapReqs, sow?.otherCapReqsFulfilled);
  const isInstallCancelled = isInstallServiceAppointmentCancelled(sow);
  const shouldFetchInstallData =
    !isInstallCancelled &&
    shouldFetchInstallSchedulingData(isPpcSchedulingNecessary);
  const serviceAppointmentId = shouldFetchInstallData
    ? getInstallServiceAppointmentId(sow)
    : undefined;
  const {
    daysOut,
    customerTimezone,
    isLoading: daysOutLoading,
    consecutiveScheduling,
    numDaysForInstall,
    retry: mutateDaysOut,
  } = useInstallSchedulingDaysOut(
    shouldFetchInstallData ? prospectId : undefined,
    serviceAppointmentId
  );
  const canFetchSlots =
    shouldFetchInstallData &&
    !!serviceAppointmentId &&
    !daysOutLoading &&
    daysOut !== undefined;
  const scheduling = usePostCapInstallScheduling(sow, prospectId, {
    isSowLoading,
    mutateSow,
    mutateDaysOut,
    slotsStartOffset: canFetchSlots ? daysOut : undefined,
    consecutiveScheduling: canFetchSlots ? consecutiveScheduling : undefined,
  });

  if (!prospectId) {
    return null;
  }

  return (
    <Row showColumnStyle={isTablet} isFirstRowTabletView={false}>
      <BaseSidebarText isTablet={isTablet}>
        <Typography variant="h4" size={docTextTitleSize}>
          {POST_CAP_INSTALL_SECTION_TITLE}
        </Typography>
        <Typography size={docTextDescriptionSize}>
          {getPostCapInstallSectionDescription(numDaysForInstall)
            .split("\n")
            .map((paragraph, index, paragraphs) => (
              <React.Fragment key={paragraph}>
                {paragraph}
                {index < paragraphs.length - 1 ? <br /> : null}
              </React.Fragment>
            ))}
        </Typography>
      </BaseSidebarText>
      <BaseContainer isTablet={isTablet}>
        <PostCapInstallScheduler
          prospectId={prospectId}
          prospectTimeZoneId={customerTimezone ?? prospect?.timeZoneId}
          bookingSnapshotLoading={
            isInstallCancelled
              ? false
              : scheduling.bookingSnapshotLoading ||
                (shouldFetchInstallData && daysOutLoading)
          }
          scheduledAppointment={
            isInstallCancelled ? undefined : scheduling.scheduledAppointment
          }
          slotBookingParentId={
            isInstallCancelled ? undefined : scheduling.slotBookingParentId
          }
          slots={
            isInstallCancelled || isPpcSchedulingNecessary
              ? []
              : scheduling.slots
          }
          slotsLoading={
            isInstallCancelled || isPpcSchedulingNecessary
              ? false
              : scheduling.slotsLoading
          }
          slotsError={isInstallCancelled ? undefined : scheduling.slotsError}
          retrySlots={scheduling.retrySlots}
          onLoadSlots={
            isInstallCancelled || isPpcSchedulingNecessary
              ? undefined
              : scheduling.requestSlots
          }
          bookingLoading={
            isInstallCancelled ? false : scheduling.bookingLoading
          }
          rescheduleLoading={
            isInstallCancelled ? false : scheduling.rescheduleLoading
          }
          bookSlot={scheduling.bookSlot}
          rescheduleAppointment={scheduling.rescheduleAppointment}
          daysOut={daysOut ?? DEFAULT_INSTALL_SCHEDULING_DAYS_OUT}
          pickerResetKey={scheduling.pickerResetKey}
          canSchedule={canScheduleInstall}
          emptySlotsMessage={
            isPpcSchedulingNecessary
              ? POST_CAP_INSTALL_PPC_PREREQUISITE_MESSAGE
              : POST_CAP_INSTALL_EMPTY_SLOTS_MESSAGE
          }
          slotPickerWarning={
            isInstallCancelled
              ? {
                  message: POST_CAP_INSTALL_CANCELLED_WARNING_MESSAGE,
                  testId: POST_CAP_INSTALL_TEST_IDS.cancelledWarning,
                  showSupportPhone: true,
                }
              : undefined
          }
          showStartTimeOnly={consecutiveScheduling}
        />
      </BaseContainer>
    </Row>
  );
};

const BaseSidebarText = styled.div<{ isTablet: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ isTablet }) => (isTablet ? "100%" : "30%")};
  gap: ${({ isTablet }) => (isTablet ? "4px" : "12px")};
`;

const BaseContainer = styled.div<{ isTablet: boolean }>`
  width: ${({ isTablet }) => (isTablet ? "100%" : "70%")};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div<{
  showColumnStyle: boolean;
  isFirstRowTabletView: boolean;
}>`
  padding-top: ${({ isFirstRowTabletView }) =>
    isFirstRowTabletView ? "12px" : "40px"};
  display: flex;
  flex-direction: ${({ showColumnStyle }) =>
    showColumnStyle ? "column" : "row"};
  gap: 12px;
`;

export { PostCapInstallSection };
export type { PostCapInstallSectionProps };
